import { Injectable, Signal } from '@angular/core'
import { RemoteData, selectWith } from '@ftr/foundation'
import { Action, Selector, State, StateContext, createSelector } from '@ngxs/store'
import { memoize } from 'lodash-es'
import { FeatureFlagKeyOfType, LaunchDarklyFlagValue, WebFeatureFlags } from '../feature-flags'
import { SetFeatureFlagValues } from './feature-flag.actions'
import { FeatureFlagStateModel } from './feature-flag.model'

export function defaultFeatureFlagState(): FeatureFlagStateModel {
  return {
    featureFlags: {
      'test-boolean-flag': false,
      'recording-vault-support': false,
      'recording-vault-activity-log-liveness': false,
      'disable-chromium-remapping-hack': false,
      'feature-disable-channel-remapping': false,
      'real-time-stt-attribution-approach': 'never-use-software-based-attribution',
      'design-system-overlay': false,
      'detailed-error-messages': false,
      'rth-feature-show-infrastructure-status': false,
      'rth-hybrid-audio-routing': false,
      'rth-courtroom-display': false,
      'trm-recording-channel-map-strategy': 0,
      'rth-join-courtroom-remotely': false,
      'realtime-intelligent-playback-layout': false,
      'realtime-intelligent-video-splitting': false,
      'stt-debug-ui-tooling': false,
      'feature-cases-hearings-audit-update-nov-24': false,
      'stt-responsive-container-layout': false,
      'rth-schedule-proceedings': false,
      'rth-configure-video-devices': false,
      'feature-department-activity-pages': false,
      'new-audit-export-structure': false,
      'feature-people-judges-setup': false,
      'rth-display-debug': false,
      'feature-people-judges-display': false,
      'realtime-recording-on-demand-deletion': false,
      'realtime-recording-automated-deletion': false,
      'feature-people-judges-annotate-sessions': false,
      'reporting-foundation-feature': false,
      'reporting-export': false,
      'reporting-subscriptions': false,
      'reporting-share': false,
      'revoke-refresh-token': false,
      'feature-annotations-manage-case-types': false,
      'rth-defined-channel-audio-emission': 0, // 0 = hybrid recorder emission disabled
      'courtroom-session-api': false,
      'departments-for-realtime': false,
      'upload-and-play': true,
      'tafkar-tiers-feature': false,
      'feature-automatic-annotations': 'off',
      'rth-enabled-realtime-hearings': false,
      'rtstt-multi-language-detection-support': false,
      'release-partial-export': false,
      'configure-departments-permission-feature': false,
      'enable-navigation-loader': false,
      'feature-annotations-assign-case-types': false,
      'stt-ui-product-rebrand': false,
      'realtime-monitoring-feature': false,
      'rth-manage-courtroom': false,
      'feature-realtime-stt-regeneration': false,
    },
    hasInitializedNonDefaultFlagValues: false,
  }
}

@State<FeatureFlagStateModel>({
  name: 'featureFlagState',
  defaults: defaultFeatureFlagState(),
})
@Injectable()
export class FeatureFlagState {
  @Selector()
  static allFeatureFlags(state: FeatureFlagStateModel): WebFeatureFlags {
    return state.featureFlags
  }

  static featureFlag = memoize(
    <T extends LaunchDarklyFlagValue = boolean>(
      key: FeatureFlagKeyOfType<T>,
    ): ((featureFlags: ReturnType<typeof FeatureFlagState.allFeatureFlags>) => T) => {
      return createSelector(
        [FeatureFlagState.allFeatureFlags],
        featureFlags => featureFlags[key as keyof WebFeatureFlags] as T,
      )
    },
  )

  static resolvedFeatureFlagValue = memoize(
    <T extends LaunchDarklyFlagValue = boolean>(
      key: FeatureFlagKeyOfType<T>,
    ): ((state: FeatureFlagStateModel) => RemoteData<T>) => {
      return createSelector([FeatureFlagState], state => {
        if (!state.hasInitializedNonDefaultFlagValues) {
          return RemoteData.loading()
        }
        return RemoteData.success(state.featureFlags[key as keyof WebFeatureFlags] as T)
      })
    },
  )

  @Action(SetFeatureFlagValues)
  setFeatureFlagValues(
    { patchState, getState }: StateContext<FeatureFlagStateModel>,
    { featureFlags, hasInitializedNonDefaultFlagValues }: SetFeatureFlagValues,
  ): void {
    const { featureFlags: currentFlags } = getState()
    const mergeFlags = <T extends LaunchDarklyFlagValue>(flagName: FeatureFlagKeyOfType<T>): T =>
      (featureFlags[flagName as keyof WebFeatureFlags] as T | undefined) ??
      (currentFlags[flagName as keyof WebFeatureFlags] as T)

    patchState({
      featureFlags: {
        'test-boolean-flag': mergeFlags('test-boolean-flag'),
        'recording-vault-support': mergeFlags('recording-vault-support'),
        'recording-vault-activity-log-liveness': mergeFlags('recording-vault-activity-log-liveness'),
        'disable-chromium-remapping-hack': mergeFlags('disable-chromium-remapping-hack'),
        'feature-disable-channel-remapping': mergeFlags('feature-disable-channel-remapping'),
        'real-time-stt-attribution-approach': mergeFlags('real-time-stt-attribution-approach'),
        'design-system-overlay': mergeFlags('design-system-overlay'),
        'detailed-error-messages': mergeFlags('detailed-error-messages'),
        'rth-feature-show-infrastructure-status': mergeFlags('rth-feature-show-infrastructure-status'),
        'rth-hybrid-audio-routing': mergeFlags('rth-hybrid-audio-routing'),
        'rth-courtroom-display': mergeFlags('rth-courtroom-display'),
        'trm-recording-channel-map-strategy': mergeFlags('trm-recording-channel-map-strategy'),
        'rth-join-courtroom-remotely': mergeFlags('rth-join-courtroom-remotely'),
        'realtime-intelligent-playback-layout': mergeFlags('realtime-intelligent-playback-layout'),
        'realtime-intelligent-video-splitting': mergeFlags('realtime-intelligent-video-splitting'),
        'stt-debug-ui-tooling': mergeFlags('stt-debug-ui-tooling'),
        'feature-cases-hearings-audit-update-nov-24': mergeFlags('feature-cases-hearings-audit-update-nov-24'),
        'stt-responsive-container-layout': mergeFlags('stt-responsive-container-layout'),
        'rth-schedule-proceedings': mergeFlags('rth-schedule-proceedings'),
        'rth-configure-video-devices': mergeFlags('rth-configure-video-devices'),
        'feature-department-activity-pages': mergeFlags('feature-department-activity-pages'),
        'new-audit-export-structure': mergeFlags('new-audit-export-structure'),
        'feature-people-judges-setup': mergeFlags('feature-people-judges-setup'),
        'rth-display-debug': mergeFlags('rth-display-debug'),
        'feature-people-judges-display': mergeFlags('feature-people-judges-display'),
        'realtime-recording-on-demand-deletion': mergeFlags('realtime-recording-on-demand-deletion'),
        'realtime-recording-automated-deletion': mergeFlags('realtime-recording-automated-deletion'),
        'feature-people-judges-annotate-sessions': mergeFlags('feature-people-judges-annotate-sessions'),
        'reporting-foundation-feature': mergeFlags('reporting-foundation-feature'),
        'reporting-export': mergeFlags('reporting-export'),
        'reporting-subscriptions': mergeFlags('reporting-subscriptions'),
        'reporting-share': mergeFlags('reporting-share'),
        'revoke-refresh-token': mergeFlags('revoke-refresh-token'),
        'feature-annotations-manage-case-types': mergeFlags('feature-annotations-manage-case-types'),
        'rth-defined-channel-audio-emission': mergeFlags('rth-defined-channel-audio-emission'),
        'courtroom-session-api': mergeFlags('courtroom-session-api'),
        'departments-for-realtime': mergeFlags('departments-for-realtime'),
        'upload-and-play': mergeFlags('upload-and-play'),
        'tafkar-tiers-feature': mergeFlags('tafkar-tiers-feature'),
        'configure-departments-permission-feature': mergeFlags('configure-departments-permission-feature'),
        'feature-automatic-annotations': mergeFlags('feature-automatic-annotations'),
        'rth-enabled-realtime-hearings': mergeFlags('rth-enabled-realtime-hearings'),
        'rtstt-multi-language-detection-support': mergeFlags('rtstt-multi-language-detection-support'),
        'release-partial-export': mergeFlags('release-partial-export'),
        'enable-navigation-loader': mergeFlags('enable-navigation-loader'),
        'feature-annotations-assign-case-types': mergeFlags('feature-annotations-assign-case-types'),
        'stt-ui-product-rebrand': mergeFlags('stt-ui-product-rebrand'),
        'realtime-monitoring-feature': mergeFlags('realtime-monitoring-feature'),
        'rth-manage-courtroom': mergeFlags('rth-manage-courtroom'),
        'feature-realtime-stt-regeneration': mergeFlags('feature-realtime-stt-regeneration'),
      },
      hasInitializedNonDefaultFlagValues,
    })
  }
}

/**
 * Wrapper over the feature flag select which provide a signal for a specific feature flag.
 * @see import('../testing/jest/mock-feature-flag').mockFeatureFlag
 */
export function featureFlag<K extends keyof WebFeatureFlags>(key: K): Signal<WebFeatureFlags[K]> {
  return selectWith(() => FeatureFlagState.featureFlag(key as FeatureFlagKeyOfType<any>))
}
